import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, CircularProgress, Tabs, Tab } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../store";
import { fetchUsers } from "../store/features/userSlice";
import { createUserApi, resetUserPasswordApi, updateUserRoleApi } from "../api/usersApi";
import UserTable from "../components/users/UserTable";
import CreateUserForm from "../components/users/CreateUserForm";
import AdminResetPasswordModal from "../components/users/AdminResetPasswordModal";
import EditUserRoleModal from "../components/users/EditUserRoleModal";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import { toastEmitter } from "../utils/toastEmitter";
import { getErrorMessage } from "../utils/errorHandling";
import { isUserAdmin, UserRole } from "../types/user";
import type { User } from "../types/user";
import { TestId } from "../constants/testIds";

type TranslationKeys = keyof typeof translations.en;
type UsersTab = "staff" | "drivers";

const isDriverRole = (role: string | undefined): boolean => role === UserRole.DRIVER;

const tabForRole = (role: string): UsersTab => (isDriverRole(role) ? "drivers" : "staff");

const UsersPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, users, usersLoading, usersError } = useSelector((state: RootState) => state.user);
  const [activeTab, setActiveTab] = useState<UsersTab>("staff");
  const [open, setOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState<User | null>(null);
  const [editRoleError, setEditRoleError] = useState<string | null>(null);
  const { t } = useTranslation();

  const isAdmin = isUserAdmin(user);

  const { staffUsers, driverUsers } = useMemo(() => {
    const staff: User[] = [];
    const drivers: User[] = [];
    for (const listedUser of users) {
      if (isDriverRole(listedUser.role)) {
        drivers.push(listedUser);
      } else {
        staff.push(listedUser);
      }
    }
    return { staffUsers: staff, driverUsers: drivers };
  }, [users]);

  const visibleUsers = activeTab === "drivers" ? driverUsers : staffUsers;
  const defaultCreateRole = activeTab === "drivers" ? UserRole.DRIVER : UserRole.STAFF;

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleOpen = () => {
    setOpen(true);
    setAddError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setAddError(null);
  };

  const handleSubmit = async (userData: {
    _id: string;
    name: string;
    role: string;
    password: string;
  }) => {
    try {
      await createUserApi(userData);
      await dispatch(fetchUsers()).unwrap();
      setActiveTab(tabForRole(userData.role));
      toastEmitter.emit(t("User created successfully" as TranslationKeys), "success");
      handleClose();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "createUser");
      setAddError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    }
  };

  const handleResetPassword = (userId: string) => {
    setSelectedUser(userId);
    setResetPasswordModalOpen(true);
    setResetPasswordError(null);
  };

  const handleResetPasswordSubmit = async (userId: string, newPassword: string) => {
    try {
      await resetUserPasswordApi(userId, newPassword);
      await dispatch(fetchUsers()).unwrap();
      toastEmitter.emit(t("Password reset successfully" as TranslationKeys), "success");
      setResetPasswordModalOpen(false);
      setSelectedUser(null);
      setResetPasswordError(null);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "resetPassword");
      setResetPasswordError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    }
  };

  const handleEditRole = (userToEdit: User) => {
    setSelectedUserForRole(userToEdit);
    setEditRoleModalOpen(true);
    setEditRoleError(null);
  };

  const handleEditRoleSubmit = async (userId: string, role: string) => {
    try {
      await updateUserRoleApi(userId, role);
      await dispatch(fetchUsers()).unwrap();
      setActiveTab(tabForRole(role));
      toastEmitter.emit(t("Role updated successfully" as TranslationKeys), "success");
      setEditRoleModalOpen(false);
      setSelectedUserForRole(null);
      setEditRoleError(null);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t);
      setEditRoleError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    }
  };

  if (usersLoading) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (usersError) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error" sx={{ mt: 3 }}>
          {usersError}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {t("Users" as TranslationKeys)}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          disabled={!isAdmin}
          data-testid={TestId.USERS.ADD_BUTTON}
        >
          {t("Add User" as TranslationKeys)}
        </Button>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_event, value: UsersTab) => setActiveTab(value)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab
          label={t("users.tab.staff" as TranslationKeys)}
          value="staff"
          data-testid={TestId.USERS.TAB_STAFF}
        />
        <Tab
          label={t("users.tab.drivers" as TranslationKeys)}
          value="drivers"
          data-testid={TestId.USERS.TAB_DRIVERS}
        />
      </Tabs>

      <UserTable
        users={visibleUsers}
        currentUser={user}
        onResetPassword={handleResetPassword}
        onEditRole={handleEditRole}
        canMutate={isAdmin}
      />

      <CreateUserForm
        open={open}
        onClose={handleClose}
        onSubmit={handleSubmit}
        error={addError}
        defaultRole={defaultCreateRole}
      />

      {selectedUser && (
        <AdminResetPasswordModal
          open={resetPasswordModalOpen}
          onClose={() => {
            setResetPasswordModalOpen(false);
            setSelectedUser(null);
            setResetPasswordError(null);
          }}
          userId={selectedUser}
          onSubmit={handleResetPasswordSubmit}
          error={resetPasswordError}
        />
      )}

      {selectedUserForRole && (
        <EditUserRoleModal
          open={editRoleModalOpen}
          onClose={() => {
            setEditRoleModalOpen(false);
            setSelectedUserForRole(null);
            setEditRoleError(null);
          }}
          user={selectedUserForRole}
          onSubmit={handleEditRoleSubmit}
          error={editRoleError}
        />
      )}
    </Box>
  );
};

export default UsersPage;
